<script setup lang="ts">
import { IDS_LOTTERIES_CLASSIC, IDS_LOTTERIES_IS_NOT_MULTIPLE_BETS, countries } from '~/utils/constants/home.js'
import BackgroundHero from '~/components/effects/BackgroundHero.vue'
import BtnTryRandom from '~/components/pages/home/BtnTryRandom.vue'
import { convertNumbersToStrings } from '~/utils/numbers.js'
import { fetchLotteries, fetchLotterySelect, fetchRulesLotterySelect } from '~/utils/playLottery.js'
import { useResultStore } from '~/store/result'
import { trackUserLocation, trackUserSelectedGame, trackRandomPlay, trackLuckTesting } from '~/tracking/homeEvents'
import { getNumbers, getOnlyNumbers, hasNumber, filterOnlyNumbersByGroup } from '~/utils/numbers'
import SelectNumbers from '~/components/pages/home/NewComponents/SelectNumbers.vue'
import HeaderNumbers from '~/components/pages/home/NewComponents/HeaderNumbers.vue'
import MultipleBetsCard from '~/components/pages/home/MultipleBetsCard.vue'
import { trackMultipleBetsToggle, trackMultipleBetsStarted } from '~/tracking/multipleBetsEvents'
import { ID_KENO_FR, ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries'
import { updateBlockNumbers } from '~/utils/lotteryNumbers'
import { getFrenchCountryPrep } from '~/utils/country-prep-fr'
import LotteryCarousel from '~/components/pages/home/LotteryCarousel.vue'
import { geoCountryCodeToCountryId } from '~/composables/useGeoCountryCode'
import { useNotificationStore } from '~/store/notification'
import { SHOW_SCAN_TICKET, SHOW_TAB_SCAN_TICKET_ALWAYS } from '~/config/featureFlags'
import { useUserLotteryStore } from '~/store/scan/lottery'
import { useAuthStore } from '~/store/auth'
import { trackLandingScannerClick, trackAuthModalOpen } from '~/tracking/scan/scanSection'
import { trackAlternativeFlowDetected } from '~/tracking/scan/scanEmptyEvents'

defineOptions({
	name: 'HomeHero',
})

const InfoPlayLottery = defineAsyncComponent(() => import('~/components/pages/results/Header/InfoPlayLottery.vue'))
const AmountPill = defineAsyncComponent(() => import('~/components/pages/home/AmountPill.vue'))
const Select = defineAsyncComponent(() => import('~/components/ui/Select.vue'))

const { $hasFeature: _$hasFeature } = useNuxtApp()
const $hasFeature = _$hasFeature ?? (() => false)
const { t: $t } = useI18n()

const router = useRouter()
const localePath = useLocalePath()

const resultStore = useResultStore()
const notificationStore = useNotificationStore()
const userLotteryStore = useUserLotteryStore()
const authStore = useAuthStore()
const { loggedIn } = storeToRefs(authStore)

interface Country {
	id: string | number
	label: string
}

interface QueryParams {
	lottery_id: number | undefined
	regular_balls?: any
	extra_balls?: any
	country_id: string | number | undefined
	page_number: number
	first_number?: any
	second_number?: any
	third_number?: any
	fourth_number?: any
	fifth_number?: any
	is_multiple_bets?: boolean
	number_of_balls?: number
	amount?: number
}

interface NumbersBlock {
	first: (string | null)[]
	last: (string | null)[] | any
}

const initialBlock = {
	first: [],
	last: [],
}

const country = ref<Country | null>(null)
const lotterySelect = ref<number | null>(null)
const codeLotteryRef = ref<any>(null)
const lotteryNumbers = ref<NumbersBlock | any>({ ...initialBlock })
const loaderSetRandomNumbers = ref(false)
const is_random = ref(false)
const showSeeNumbers = ref(false)
const homeHero = ref<HTMLElement | null>(null)
const isHeroVisible = ref(true)
const isMobile = ref(false)
const isAutoScrollMobile = ref(false)
const isActiveMutipleBets = ref(false)
const visibleFirstCount = ref(0)
const visibleLastCount = ref(0)
const multipleBetsRef = ref<{ getCombinationsValue?: () => Promise<number> } | null>(null)
const actionsSectionRef = ref<HTMLElement | null>(null)
const seeNumbersBarRef = ref<HTMLElement | null>(null)
const amount = ref(1)
let observer: IntersectionObserver

const geoCountryCode = useGeoCountryCode()
const detectedGeoCountryId = computed(() => geoCountryCodeToCountryId(geoCountryCode.value))

const isShowMultipleBets = computed(() => {
	if (lotterySelect.value === null) return false
	if (IDS_LOTTERIES_IS_NOT_MULTIPLE_BETS.includes(lotterySelect.value)) return false
	const isHasNumbers = hasNumber(lotteryNumbers.value)
	if (isHasNumbers === false) return isActiveMutipleBets.value
	return true
})

const isShowSeeNumbers = computed(() => {
	if (lotterySelect.value === null) return false
	if (!isMobile.value) return false
	if (isActiveMutipleBets.value === true) return false
	return showSeeNumbers.value && isHeroVisible.value
})

const shouldReserveSeeNumbersSpace = computed(() => {
	if (!isMobile.value) return false
	if (lotterySelect.value === null) return false
	if (isActiveMutipleBets.value === true) return false
	return true
})

const seeNumbersScrollOffset = computed(() => {
	if (!shouldReserveSeeNumbersSpace.value) return 0
	const safeGap = 12
	if (seeNumbersBarRef.value) {
		const { height } = seeNumbersBarRef.value.getBoundingClientRect()
		if (height > 0) return Math.ceil(height) + safeGap
	}
	const fallbackHeaderHeight = 240
	return fallbackHeaderHeight + safeGap
})

const isMutipleBets = computed(() => isActiveMutipleBets.value)

const getLotteries = computed(() => {
	const lotteries = fetchLotteries(country.value)
	return lotteries.map((lottery) => ({
		...lottery,
		label: lottery.id === ID_EURO_MILLIONS ? $t(lottery.name) : lottery.name,
	}))
})
const getLotterySelect = computed(() => fetchLotterySelect(lotterySelect.value))
const getLotterySelectItem = computed(() => {
	if (lotterySelect.value === null) return null
	const lottery = fetchLotterySelect(lotterySelect.value)
	if (lottery === null) return null
	return {
		id: lottery.id,
		label: lottery.name,
		image: lottery.image,
	}
})

const getRulesLotterySelect = computed(() => {
	return fetchRulesLotterySelect(getLotterySelect.value, isActiveMutipleBets.value)
})

const isRepeatNumbers = computed(() => {
	if (lotteryNumbers.value === null) return false

	const groups = ['first', 'last']
	return groups.some((group) => {
		const numbers = lotteryNumbers.value[group]
		if (!numbers || !Array.isArray(numbers)) return false

		const stringNumbers = convertNumbersToStrings(numbers)
		const uniqueNumbers = new Set(stringNumbers)
		return uniqueNumbers.size !== stringNumbers.length
	})
})

const isShowAnimation = computed(() => {
	if (lotterySelect.value === ID_KENO_FR) return false
	return isActiveMutipleBets.value ? !isActiveMutipleBets.value : true
})

const isDisabledBtn = computed(() => {
	if (!lotteryNumbers.value) return true
	if (loaderSetRandomNumbers.value) return true

	const { id, first_block } = getRulesLotterySelect.value || { id: null }

	/* Multiple Checks */
	if (isActiveMutipleBets.value && getRulesLotterySelect.value !== null) {
		const { min_count_active, first_block, last_block } = getRulesLotterySelect.value

		const firstSelected = lotteryNumbers.value.first.filter((n: number | null) => n !== null).length
		const lastSelected = lotteryNumbers.value.last
			? lotteryNumbers.value.last.filter((n: number | null) => n !== null).length
			: 0

		const countAllBalls = firstSelected + lastSelected
		const firstCondition = firstSelected >= first_block.min_select

		if (lotteryNumbers.value.last === null) {
			return !(countAllBalls >= min_count_active && firstCondition)
		}

		const lastCondition = lastSelected >= last_block.min_select

		return !(countAllBalls >= min_count_active && firstCondition && lastCondition)
	}

	/* Standar keno */
	if (id === ID_KENO_FR) {
		const firstCount = lotteryNumbers.value.first.filter((num: string | null) => num !== null).length
		return !(firstCount >= first_block.min_select)
	}
	/* Standar */
	const first = lotteryNumbers.value.first.some((num: string | null) => num === '' || num === null)
	const last = lotteryNumbers.value.last
		? lotteryNumbers.value.last.some((num: string | null) => num === '' || num === null)
		: false
	if (getRulesLotterySelect.value && IDS_LOTTERIES_CLASSIC.includes(getRulesLotterySelect.value.id)) {
		return first || last
	}
	return first || last || isRepeatNumbers.value === true
})

const getLogin = computed(() => loggedIn.value)

const updateCountry = (value: any) => {
	const shouldTrack = import.meta.client
	const { id: countryId, label, ...allItems } = value
	country.value = { id: countryId, label: $t(label), ...allItems }
	isActiveMutipleBets.value = false
	if (country.value?.id === countryId) {
		if (shouldTrack) {
			trackUserLocation({ country: $t(label) })
		}

		if (getLotteries.value.length > 1) {
			lotterySelect.value = null
			return
		}
		const { name, id: lotteryId } = getLotteries.value[0]
		if (shouldTrack) {
			trackUserSelectedGame({ game_name: name })
		}
		lotterySelect.value = lotteryId
		setNumbers()
		return
	}

	if (getLotteries.value.length > 1) {
		lotterySelect.value = null
		return
	}
	const { id: lotteryId, name } = getLotteries.value[0]
	if (shouldTrack) {
		trackUserSelectedGame({ game_name: name })
	}
	lotterySelect.value = lotteryId
	setNumbers()
}

watchEffect(() => {
	if (country.value !== null) return

	const matchedCountry = countries.find((item) => Number(item.id) === detectedGeoCountryId.value)
	if (!matchedCountry) return

	updateCountry(matchedCountry)
})

const updateLotteryNumbers = (value: any) => {
	lotteryNumbers.value = value
}

const random = async () => {
	loaderSetRandomNumbers.value = true

	if (codeLotteryRef.value === null) {
		loaderSetRandomNumbers.value = false
		return
	}
	simulateAnimationMobile()
	await codeLotteryRef.value.setRandom()

	loaderSetRandomNumbers.value = false
	is_random.value = true
	trackRandomPlay({
		country: country.value?.label,
		game_name: getLotterySelect.value?.name,
		random_numbers: getNumbers(lotteryNumbers.value)?.join(','),
	})
}

const updateLottery = (value: any) => {
	lotterySelect.value = value?.id
	const findLottery = getLotteries.value.find((lottery) => lottery.id === value.id)
	if (!findLottery) return
	isActiveMutipleBets.value = false
	setNumbers()
	trackUserSelectedGame({ game_name: findLottery.name })
}

const setQueryParams = (): QueryParams => {
	const { first, last } = lotteryNumbers.value
	const regularBalls = first.filter((n: string | null) => n !== null)
	let query: QueryParams = {
		lottery_id: getLotterySelect.value?.id,
		regular_balls: regularBalls,
		country_id: country.value?.id,
		page_number: 1,
	}

	if (last !== null) {
		query.extra_balls = last.filter((n: string | null) => n !== null)
	}

	if (isActiveMutipleBets.value) {
		query.is_multiple_bets = true
	}

	if (ID_KENO_FR === getLotterySelect.value?.id) {
		query.number_of_balls = regularBalls.length
		query.amount = amount.value
	}

	if (getLotterySelect.value && IDS_LOTTERIES_CLASSIC.includes(getLotterySelect.value?.id)) {
		const [first_number, second_number, third_number, fourth_number, fifth_number] = first
		query = {
			lottery_id: getLotterySelect.value?.id,
			country_id: country.value?.id,
			first_number,
			second_number,
			third_number,
			fourth_number,
			fifth_number,
			page_number: 1,
		}
	}

	return query
}

const showResult = async () => {
	resultStore.SET_STATE_RESULT({
		type: 'country',
		value: country.value,
	})

	resultStore.SET_STATE_RESULT({
		type: 'lottery',
		value: getLotterySelect.value,
	})

	resultStore.SET_STATE_RESULT({
		type: 'numbers',
		value: filterOnlyNumbersByGroup(lotteryNumbers.value),
	})

	resultStore.SET_STATE_RESULT({
		type: 'isMutipleBets',
		value: isActiveMutipleBets.value,
	})

	resultStore.SET_STATE_RESULT({
		type: 'amount',
		value: amount.value,
	})

	if (isActiveMutipleBets.value) {
		let combinationCount = 0
		if (multipleBetsRef.value !== null && typeof multipleBetsRef.value.getCombinationsValue === 'function') {
			combinationCount = await multipleBetsRef.value.getCombinationsValue()
			resultStore.SET_STATE_RESULT({
				type: 'combinations',
				value: combinationCount,
			})
		}

		trackMultipleBetsStarted({
			lottery_id: lotterySelect.value,
			combination_count: combinationCount,
			numbers: getOnlyNumbers(lotteryNumbers.value)?.join(','),
		})
	} else {
		trackLuckTesting({
			country: country.value?.label,
			game_name: getLotterySelect.value?.name,
			is_random: is_random.value,
			numbers: getNumbers(lotteryNumbers.value)?.join(','),
		})
	}
	const query = setQueryParams()

	router.push({ path: localePath('/results'), query: query as unknown as Record<string, any> })
}

const setPropertys = () => {
	if (resultStore.country) {
		country.value = resultStore.country
	}

	if (resultStore.lottery) {
		lotterySelect.value = (resultStore.lottery as any).id
	}

	if (resultStore.isMutipleBets) {
		setNumbers()
		return
	}

	if (resultStore.numbers) {
		lotteryNumbers.value = resultStore.numbers
	}
}

const updateBlock = (property: string, value: string, mode: string | null = null) => {
	updateBlockNumbers({
		property: property,
		blockNumbers: lotteryNumbers.value,
		rules: getRulesLotterySelect.value,
		ruleProperty: value,
		mode: mode,
		multipleBets: isActiveMutipleBets.value,
	})
}

const setNumbers = async (mode: string | null = null) => {
	updateBlock('first', 'first_block', mode)
	updateBlock('last', 'last_block', mode)
	visibleCounts(lotteryNumbers.value)

	if (codeLotteryRef.value === null) return
	await codeLotteryRef.value.resetManualAnimation()
}

const showHero = () => {
	if (homeHero.value) {
		observer = new IntersectionObserver(
			([entry]) => {
				isHeroVisible.value = entry.isIntersecting
			},
			{
				root: null,
				threshold: 0,
			},
		)

		observer.observe(homeHero.value)
	}
}

const handleScroll = () => {
	showSeeNumbers.value = window.scrollY > 700
}

const clearAll = async () => {
	if (codeLotteryRef.value === null) return
	await codeLotteryRef.value.clearAll()
}

const clearNumber = async (value: any) => {
	if (codeLotteryRef.value === null) return
	await codeLotteryRef.value.clearNumber(value)
}

const scrollToActionsSection = () => {
	if (!import.meta.client || actionsSectionRef.value === null) return
	const offset = seeNumbersScrollOffset.value
	const safeGap = 12
	const actionsTopGap = 56
	const top = actionsSectionRef.value.getBoundingClientRect().top + window.scrollY - offset - safeGap - actionsTopGap
	window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

const simulateAnimationMobile = async () => {
	if (!isShowSeeNumbers.value) return
	visibleFirstCount.value = 0
	visibleLastCount.value = 0

	const { first, last } = lotteryNumbers.value

	const firstBlockLength = first.length || 0
	const lastBlockLength = last?.length || 0

	const firstSelectedNumbers = first.filter((n: number) => n !== null)
	const lastSelectedNumbers = last ? last.filter((n: number) => n !== null) : []

	for (let i = 0; i < Math.max(firstBlockLength, firstSelectedNumbers.length); i++) {
		await new Promise((resolve) => setTimeout(resolve, 400))

		if (i < firstBlockLength) {
			visibleFirstCount.value = i + 1
		}
	}

	if (lastBlockLength === 0) return
	for (let i = 0; i < Math.max(lastBlockLength, lastSelectedNumbers.length); i++) {
		await new Promise((resolve) => setTimeout(resolve, 400))

		if (i < lastBlockLength) {
			visibleLastCount.value = i + 1
		}
	}
}

const visibleCounts = (blockNumbers: any) => {
	const conditionalShowNumbers =
		lotterySelect.value === ID_KENO_FR && getRulesLotterySelect.value !== null
			? getRulesLotterySelect.value.first_block.total
			: blockNumbers.first.length
	visibleFirstCount.value = conditionalShowNumbers
	visibleLastCount.value = blockNumbers.last?.length || 0
}

const checkScreenSize = async () => {
	const currentWidth = window.innerWidth
	isMobile.value = currentWidth <= 480
	isAutoScrollMobile.value = currentWidth <= 640
}

const updateStatusMutipleBets = () => {
	const mode = isActiveMutipleBets.value ? 'selected' : null
	setNumbers(mode)

	const contentEvent = {
		lottery_id: lotterySelect.value,
		lottery_name: getLotterySelect.value?.name,
		mode: isActiveMutipleBets.value ? 'on' : 'off',
		selected_count_main: lotteryNumbers.value.first.filter((n: number | null) => n !== null).length,
		selected_count_special:
			lotteryNumbers.value.last === null
				? 0
				: lotteryNumbers.value.last.filter((n: number | null) => n !== null).length,
		numbers: getOnlyNumbers(lotteryNumbers.value)?.join(','),
	}
	if (lotteryNumbers.value.last !== null) {
		contentEvent.selected_count_special = lotteryNumbers.value.last.filter((n: number | null) => n !== null).length
	}
	trackMultipleBetsToggle(contentEvent)
}

const openAuthWithScanTicket = async () => {
	trackLandingScannerClick({
		cta_location: 'hero',
	})
	trackAlternativeFlowDetected({
		missing_steps: ['country', 'lottery', 'numbers'].join(','),
	})
	const basePath = localePath('/results/scan')
	if ($hasFeature(SHOW_TAB_SCAN_TICKET_ALWAYS) === true) {
		await navigateTo(`${basePath}`, { replace: true })
		return
	}
	if (getLogin.value === false) {
		userLotteryStore.SET_STATE_LOTTERY({
			type: 'showModalSignUp',
			value: true,
		})
		trackAuthModalOpen({
			source: 'hero',
			default_tab: 'signup',
		})
		return
	}

	await navigateTo(`${basePath}`, { replace: true })
}

onMounted(() => {
	setPropertys()
	window.addEventListener('scroll', handleScroll)
	window.addEventListener('resize', checkScreenSize)
	showHero()
	checkScreenSize()
})

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll)
	window.removeEventListener('resize', checkScreenSize)
	if (observer && homeHero.value) {
		observer.unobserve(homeHero.value)
	}
})
</script>

<template>
	<section
		id="hero"
		class="relative z-10 home-container"
	>
		<BackgroundHero />
		<div class="flex flex-col">
			<div
				ref="homeHero"
				class="container pt-6 sm:pt-10 pb-14 space-y-11 smm:space-y-14"
				:class="{ 'sm:pb-24 lg:pb-36': country === null }"
			>
				<div class="flex justify-center">
					<div class="text-neutral-50 text-center space-y-6 smm:space-y-5 xl:space-y-8 lg:max-w-3xl 5xl:max-w-4xl">
						<UButton
							v-if="$hasFeature(SHOW_SCAN_TICKET)"
							color="white"
							variant="ghost"
							class="flex justify-between items-center gap-2 text-primary-500 py-2 px-4 rounded-full bg-neutral-50 min-w-72 mx-auto hover:bg-opacity-90 transition-colors cursor-pointer w-fit mb-10 sm:mb-0"
							:ui="{ base: 'w-full' }"
							@click="openAuthWithScanTicket"
						>
							<template #leading>
								<UIcon
									name="i-mdi:line-scan"
									class="flex-shrink-0 size-4"
								/>
							</template>

							<span class="font-medium text-sm"
								><span class="font-semibold">{{ $t('New') }}: </span>{{ $t('Scan your physical ticket') }}</span
							>

							<template #trailing>
								<UIcon
									name="i-tabler:circle-arrow-right-filled"
									class="flex-shrink-0 size-4"
								/>
							</template>
						</UButton>

						<h1 class="text-xl lg:text-4xl 5xl:text-5xl font-bold sm:font-semibold mx-auto smm:mx-0 px-0 smm:px-5">
							{{ $t('Did you know you could have been a millionaire') }}?
						</h1>
						<p class="text-base sm:text-[18px] mx-auto smm:mx-0 px-12 smm:px-0">
							{{ $t('Check your numbers and never miss out again') }}:
						</p>
					</div>
				</div>
				<div class="flex flex-col justify-center items-center gap-12">
					<Select
						:value="country || undefined"
						:list="countries"
						:translate="true"
						bg-list="bg-gradient-green"
						class="select-custom"
						@change="updateCountry"
					/>
					<div
						v-if="country !== null && getLotteries.length > 0"
						class="space-y-6 w-full flex flex-col items-center"
					>
						<Select
							:value="getLotterySelectItem ?? undefined"
							:disabled="getLotteries.length === 1"
							:list="getLotteries"
							:is-lottery="true"
							:show-icon-leading="getLotterySelectItem !== null"
							bg-list="bg-gradient-green"
							:placeholder="$t('Select a lottery')"
							class="select-custom max-w-xl"
							@change="updateLottery"
						>
							<template #icon-leading>
								<InfoPlayLottery :lottery="getLotterySelectItem">
									<template #icon>
										<UIcon
											name="i-material-symbols:info-outline-rounded"
											class="size-6 pl-5 text-white cursor-pointer hover:text-white/80"
										/>
									</template>
								</InfoPlayLottery>
							</template>
						</Select>

						<div
							class="flex flex-col items-start sm:items-center justify-center text-neutral-50 rounded-xl bg-[#06693F] bg-opacity-15 py-5 px-2 max-w-2xl w-full sm:text-center"
						>
							<div class="flex items-center gap-2 smm:gap-2.5">
								<p
									v-if="getLotteries.length === 1"
									class="text-sm text-left flex-1"
								>
									{{ $t(country.label) }}
									{{ $t('has only one lottery available. It has been automatically selected for you') }}.
								</p>
								<p
									v-else
									class="text-sm text-left flex-1"
								>
									{{
										$t('Only lotteries available in {country} can be selected. Please choose at least one', {
											country: $t(country?.label),
											prep: getFrenchCountryPrep(country?.id),
										})
									}}.
								</p>
							</div>
						</div>
					</div>

					<div
						v-if="lotterySelect !== null"
						class="space-y-12"
					>
						<div
							id="selectNumbers"
							class="space-y-6"
						>
							<h2 class="text-neutral-50 text-center text-base font-semibold max-w-md mx-auto leading-6 smm:leading-9">
								{{ $t('Select your lucky numbers below') }}:
							</h2>
							<SelectNumbers
								ref="codeLotteryRef"
								v-model="lotteryNumbers"
								:rules="getRulesLotterySelect || {}"
								:show-class-animation="!isShowSeeNumbers"
								:show-header-numbers="!isActiveMutipleBets"
								:is-mutiple-bets="isMutipleBets"
								:show-animation-floating-balls="isShowAnimation"
								:enable-auto-scroll-on-first-limit="isAutoScrollMobile"
								:scroll-top-offset="seeNumbersScrollOffset"
								@update="updateLotteryNumbers"
								@scroll-to-actions="scrollToActionsSection"
							>
								<template #extra-section>
									<MultipleBetsCard
										v-if="isShowMultipleBets"
										ref="multipleBetsRef"
										v-model="isActiveMutipleBets"
										class="mx-auto max-w-3xl"
										:numbers="lotteryNumbers"
										:rules="getRulesLotterySelect"
										@update-status-mutiple-bets="updateStatusMutipleBets"
									/>
									<AmountPill
										v-if="lotterySelect === ID_KENO_FR"
										v-model="amount"
										:show-title="true"
										:options="[1, 2, 3, 5, 10]"
										class="mx-auto max-w-[350px]"
									/>
								</template>
							</SelectNumbers>
						</div>
						<div
							ref="actionsSectionRef"
							class="flex flex-col smm:flex-row justify-center gap-8 smm:gap-12"
						>
							<BtnTryRandom
								:on-click="random"
								:loading="loaderSetRandomNumbers"
							/>
							<UButton
								:disabled="isDisabledBtn"
								color="white"
								variant="soft"
								name="label"
								trailing
								:label="$t('Check past results')"
								:ui="{
									rounded: 'rounded-full',
								}"
								class="min-w-60 min-h-12 text-base font-semibold flex justify-center"
								:class="{
									'hover:bg-opacity-90 bg-primary-50 border-none text-success-600': !isDisabledBtn,
									'border border-neutral-400 bg-neutral-500 text-white': isDisabledBtn,
								}"
								:loading="resultStore.loading"
								@click="showResult"
							>
								<template #leading>
									<UIcon
										name="i-mage:stars-c"
										class="w-6 h-6"
									/>
								</template>
							</UButton>
						</div>
					</div>
				</div>
			</div>
			<div
				v-if="isShowSeeNumbers"
				ref="seeNumbersBarRef"
				class="fixed top-0 w-full smm:hidden bg-[#3ab03f] py-5 z-20"
			>
				<div class="container space-y-6">
					<h2 class="text-neutral-50 text-center text-base font-semibold max-w-md mx-auto leading-6 smm:leading-9">
						{{ $t('Select your lucky numbers below') }}:
					</h2>
					<HeaderNumbers
						class="flex flex-col items-center gap-5"
						class-container-numbers="flex justify-center items-center flex-wrap w-fit mx-auto"
						:block-numbers="lotteryNumbers"
						:rules="getRulesLotterySelect"
						:visible-first-count="visibleFirstCount"
						:visible-last-count="visibleLastCount"
						@clear-number="clearNumber"
						@clear-all="clearAll"
					/>
				</div>
			</div>
			<LotteryCarousel v-if="notificationStore.getLasLotteries.length > 0" />
		</div>
	</section>
</template>
